import {
  Button,
  Dialog,
  Field,
  Input,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';
import { toaster } from '@mono/chakra';
import { isHttpError, useHttp } from '@mono/http-hook';
import { FC, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

type FormValues = {
  name: string;
};

type CreateFolderButtonProps = {
  organizationId: string;
  projectId: string;
  parentPath: string;
  onCreated: () => void;
  isDisabled?: boolean;
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (isHttpError(error)) {
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object') {
      const { error: errorMessage, message: responseMessage } =
        responseData as { error?: string; message?: string };
      return errorMessage ?? responseMessage ?? error.message ?? fallback;
    }
    return error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const CreateFolderButton: FC<CreateFolderButtonProps> = ({
  organizationId,
  projectId,
  parentPath,
  onCreated,
  isDisabled,
}) => {
  const http = useHttp();
  const [isOpen, setIsOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState,
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
    },
  });

  const closeDialog = () => {
    setIsOpen(false);
    setServerError(null);
    reset({ name: '' });
  };

  const openDialog = () => {
    if (isDisabled) return;
    setIsOpen(true);
  };

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    setServerError(null);

    try {
      await http.post(
        `/api/v1/organizations/${organizationId}/projects/${projectId}/file-tree/folders`,
        {
          parentPath,
          name: values.name.trim(),
        },
        { withCredentials: true }
      );

      toaster.create({
        type: 'success',
        title: 'Folder created',
        description: `${values.name.trim()} is now available.`,
      });

      onCreated();
      closeDialog();
    } catch (error) {
      const message = getErrorMessage(error, 'Unable to create folder.');
      setServerError(message);
      toaster.create({
        type: 'error',
        title: 'Failed to create folder',
        description: message,
      });
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={openDialog}
        disabled={isDisabled}
      >
        New folder
      </Button>
      <Dialog.Root
        open={isOpen}
        onOpenChange={({ open }) => {
          if (!open) closeDialog();
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.CloseTrigger />
              <Dialog.Header flexDirection="column" gap="1">
                <Dialog.Title>Create folder</Dialog.Title>
                <Dialog.Description>
                  Add a new folder inside{' '}
                  <Text as="span" fontWeight="medium">
                    {parentPath || '/'}
                  </Text>
                  .
                </Dialog.Description>
              </Dialog.Header>
              <Dialog.Body>
                <form id="create-folder-form" onSubmit={handleSubmit(onSubmit)}>
                  <Stack gap="4">
                    <Field.Root invalid={Boolean(formState.errors.name)}>
                      <Field.Label>Folder name</Field.Label>
                      <Input
                        autoFocus
                        placeholder="Design specs"
                        {...register('name', {
                          required: 'Folder name is required.',
                          validate: (value) =>
                            value.includes('/') || value.includes('\\')
                              ? 'Folder name cannot contain slashes.'
                              : true,
                        })}
                      />
                      <Field.HelperText>
                        Avoid characters like / or \.
                      </Field.HelperText>
                      <Field.ErrorText>
                        {formState.errors.name?.message}
                      </Field.ErrorText>
                    </Field.Root>

                    {serverError ? (
                      <Text color="fg.error" fontSize="sm">
                        {serverError}
                      </Text>
                    ) : null}
                  </Stack>
                </form>
              </Dialog.Body>
              <Dialog.Footer gap="3" justifyContent="flex-end">
                <Button
                  variant="ghost"
                  onClick={closeDialog}
                  disabled={formState.isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="create-folder-form"
                  loading={formState.isSubmitting}
                  colorPalette="primary"
                >
                  Create folder
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
};

export default CreateFolderButton;
